import { useCallback, useState } from 'react';
import type { ItemQuantities } from '../helpers/assignment-helpers';
import { generateChunkId, initializeItemQuantities, convertFromAssignments, mergeChunksInPackage } from '../helpers/assignment-helpers';
import type { OrderItem, Package } from '../types';

export const useItemAssignments = (items: OrderItem[], initialPackages: Package[]) => {
  const [itemQuantities, setItemQuantities] = useState<ItemQuantities>(() => {
    const hasAssignments = initialPackages.some((pkg) => pkg.assignedItems && pkg.assignedItems.length > 0);
    if (hasAssignments) {
      return convertFromAssignments(items, initialPackages);
    }
    return initializeItemQuantities(items);
  });

  const assignItemToPackage = useCallback((itemId: string, chunkId: string, packageIndex: number, orderInPackage?: number) => {
    setItemQuantities((prev) => {
      const currentQuantities = prev[itemId];
      const chunkIndex = currentQuantities.chunks.findIndex((c) => c.chunkId === chunkId);

      if (chunkIndex === -1 || currentQuantities.chunks[chunkIndex].packageIndex !== undefined) {
        return prev;
      }

      const order =
        orderInPackage ??
        Object.values(prev)
          .flatMap((item) => item.chunks)
          .filter((c) => c.packageIndex === packageIndex).length;

      const newState = { ...prev };

      Object.entries(newState).forEach(([id, data]) => {
        const updatedChunks = data.chunks.map((chunk) => {
          if (chunk.packageIndex === packageIndex && chunk.orderInPackage !== undefined && chunk.orderInPackage >= order) {
            return { ...chunk, orderInPackage: chunk.orderInPackage + 1 };
          }
          return chunk;
        });
        newState[id] = { ...data, chunks: updatedChunks };
      });

      const newChunks = [...newState[itemId].chunks];
      newChunks[chunkIndex] = { ...newChunks[chunkIndex], packageIndex, orderInPackage: order };

      newState[itemId] = {
        ...newState[itemId],
        chunks: newChunks,
      };

      return mergeChunksInPackage(newState, itemId, packageIndex);
    });
  }, []);

  const unassignItemFromPackage = useCallback((itemId: string, chunkId: string) => {
    setItemQuantities((prev) => {
      const currentQuantities = prev[itemId];
      const chunkIndex = currentQuantities.chunks.findIndex((c) => c.chunkId === chunkId);

      if (chunkIndex === -1) {
        return prev;
      }

      const removedChunk = currentQuantities.chunks[chunkIndex];
      const fromPackage = removedChunk.packageIndex;
      const fromOrder = removedChunk.orderInPackage;

      const newState = { ...prev };

      if (fromPackage !== undefined && fromOrder !== undefined) {
        Object.entries(newState).forEach(([id, data]) => {
          const updatedChunks = data.chunks.map((chunk) => {
            if (chunk.packageIndex === fromPackage && chunk.orderInPackage !== undefined && chunk.orderInPackage > fromOrder) {
              return { ...chunk, orderInPackage: chunk.orderInPackage - 1 };
            }
            return chunk;
          });
          newState[id] = { ...data, chunks: updatedChunks };
        });
      }

      const newChunks = [...newState[itemId].chunks];
      newChunks[chunkIndex] = { ...newChunks[chunkIndex], packageIndex: undefined, orderInPackage: undefined };

      newState[itemId] = {
        ...newState[itemId],
        chunks: newChunks,
      };

      return newState;
    });
  }, []);

  const moveItemBetweenPackages = useCallback((itemId: string, chunkId: string, toPackage: number, orderInPackage?: number) => {
    setItemQuantities((prev) => {
      const currentQuantities = prev[itemId];
      const chunkIndex = currentQuantities.chunks.findIndex((c) => c.chunkId === chunkId);

      if (chunkIndex === -1) {
        return prev;
      }

      const currentChunk = currentQuantities.chunks[chunkIndex];
      const fromPackage = currentChunk.packageIndex;
      const fromOrder = currentChunk.orderInPackage;

      const order =
        orderInPackage ??
        Object.values(prev)
          .flatMap((item) => item.chunks)
          .filter((c) => c.packageIndex === toPackage && c.chunkId !== chunkId).length;

      const newState = { ...prev };

      const isSamePackage = fromPackage === toPackage;

      if (isSamePackage && fromOrder !== undefined) {
        Object.entries(newState).forEach(([id, data]) => {
          const updatedChunks = data.chunks.map((chunk) => {
            if (chunk.packageIndex === toPackage && chunk.orderInPackage !== undefined && chunk.chunkId !== chunkId) {
              if (fromOrder < order && chunk.orderInPackage > fromOrder && chunk.orderInPackage <= order) {
                return { ...chunk, orderInPackage: chunk.orderInPackage - 1 };
              }
              if (fromOrder > order && chunk.orderInPackage >= order && chunk.orderInPackage < fromOrder) {
                return { ...chunk, orderInPackage: chunk.orderInPackage + 1 };
              }
            }
            return chunk;
          });
          newState[id] = { ...data, chunks: updatedChunks };
        });
      } else {
        if (fromPackage !== undefined && fromOrder !== undefined) {
          Object.entries(newState).forEach(([id, data]) => {
            const updatedChunks = data.chunks.map((chunk) => {
              if (chunk.packageIndex === fromPackage && chunk.orderInPackage !== undefined && chunk.orderInPackage > fromOrder) {
                return { ...chunk, orderInPackage: chunk.orderInPackage - 1 };
              }
              return chunk;
            });
            newState[id] = { ...data, chunks: updatedChunks };
          });
        }

        Object.entries(newState).forEach(([id, data]) => {
          const updatedChunks = data.chunks.map((chunk) => {
            if (
              chunk.packageIndex === toPackage &&
              chunk.orderInPackage !== undefined &&
              chunk.orderInPackage >= order &&
              chunk.chunkId !== chunkId
            ) {
              return { ...chunk, orderInPackage: chunk.orderInPackage + 1 };
            }
            return chunk;
          });
          newState[id] = { ...data, chunks: updatedChunks };
        });
      }

      const newChunks = [...newState[itemId].chunks];
      newChunks[chunkIndex] = { ...newChunks[chunkIndex], packageIndex: toPackage, orderInPackage: order };

      newState[itemId] = {
        ...newState[itemId],
        chunks: newChunks,
      };

      return isSamePackage ? newState : mergeChunksInPackage(newState, itemId, toPackage);
    });
  }, []);

  const updateChunkQuantity = useCallback((itemId: string, chunkId: string, newQuantity: number) => {
    setItemQuantities((prev) => {
      const currentQuantities = prev[itemId];
      if (!currentQuantities) {
        return prev;
      }

      const chunkIndex = currentQuantities.chunks.findIndex((c) => c.chunkId === chunkId);

      if (chunkIndex === -1 || newQuantity < 1) {
        return prev;
      }

      const chunk = currentQuantities.chunks[chunkIndex];
      const currentQuantity = chunk.quantity;
      const quantityDelta = newQuantity - currentQuantity;

      if (quantityDelta === 0) {
        return prev;
      }

      if (quantityDelta < 0) {
        const reducedAmount = Math.abs(quantityDelta);
        const newChunks = [...currentQuantities.chunks];
        newChunks[chunkIndex] = { ...chunk, quantity: newQuantity };
        newChunks.splice(chunkIndex + 1, 0, { chunkId: generateChunkId(), quantity: reducedAmount });

        return {
          ...prev,
          [itemId]: {
            ...currentQuantities,
            chunks: newChunks,
          },
        };
      }

      let remaining = quantityDelta;
      const newChunks = [...currentQuantities.chunks];
      const chunksToRemove: string[] = [];

      for (let i = 0; i < newChunks.length && remaining > 0; i++) {
        if (i === chunkIndex) {
          continue;
        }

        const otherChunk = newChunks[i];

        if (otherChunk.packageIndex !== undefined) {
          continue;
        }

        const canTake = Math.min(otherChunk.quantity, remaining);
        remaining -= canTake;

        if (otherChunk.quantity === canTake) {
          chunksToRemove.push(otherChunk.chunkId);
        } else {
          newChunks[i] = { ...otherChunk, quantity: otherChunk.quantity - canTake };
        }
      }

      if (remaining > 0) {
        return prev;
      }

      const updatedChunkIndex = newChunks.findIndex((c) => c.chunkId === chunkId);
      if (updatedChunkIndex !== -1) {
        newChunks[updatedChunkIndex] = { ...newChunks[updatedChunkIndex], quantity: newQuantity };
      }

      const finalChunks = newChunks.filter((c) => !chunksToRemove.includes(c.chunkId));

      return {
        ...prev,
        [itemId]: {
          ...currentQuantities,
          chunks: finalChunks,
        },
      };
    });
  }, []);

  const reindexPackagesAfterRemoval = useCallback((removedPackageIndex: number) => {
    setItemQuantities((prev) => {
      const newState = { ...prev };

      Object.entries(newState).forEach(([itemId, quantities]) => {
        const updatedChunks = quantities.chunks.map((chunk) => {
          if (chunk.packageIndex !== undefined && chunk.packageIndex > removedPackageIndex) {
            return { ...chunk, packageIndex: chunk.packageIndex - 1 };
          }
          return chunk;
        });
        newState[itemId] = { ...quantities, chunks: updatedChunks };
      });

      return newState;
    });
  }, []);

  return {
    itemQuantities,
    assignItemToPackage,
    unassignItemFromPackage,
    moveItemBetweenPackages,
    updateChunkQuantity,
    reindexPackagesAfterRemoval,
  };
};
